import { Box, Link, Typography } from "@mui/material";
import { TableChartOutlined as SheetIcon, PictureAsPdfOutlined as PdfIcon } from "@mui/icons-material";
import {
  TargetSchema,
  type ClaimantProjection,
  type ClaimItemRowForecast,
  type ExtractedRecord,
  type PolicyWindows,
} from "@/types";
import { outsidePolicyLineFor, outsideWindowPhrase } from "../../utils/outsidePolicy";
import { formatUSD } from "@/utils/formatters/currencyFormatter";
import { DATE_FORMATS } from "@/utils/formatters/dateFormatter";
import { getExcelDate } from "@/utils/helpers/getExcelDate";
import { getRecordData } from "../../utils/recordUtils";
import { parseMoneyCell } from "../../utils/amounts";
import { relationshipLabel } from "./claimantGridCells";

/**
 * Expanded claimant panel (#7435, artboard 2a): one fact line, then the
 * claimant's claim lines as a flat list — Service date · Paid date · Procedure
 * · Paid · Source — instead of the generic boxed nested table with whatever
 * columns the file happened to map. One status column, "Counts toward
 * deductible" (#7436 AC3): Yes, "No · before incurred period" (warning tone
 * until the reviewer confirms the dates on the rail), "On file" (the loader
 * skips it as already present) or "Excluded".
 */

interface ClaimLinesPanelProps {
  parentRecord: ExtractedRecord;
  lines: ExtractedRecord[];
  projection?: ClaimantProjection;
  getClaimItemRowForRecord?: (record: ExtractedRecord) => ClaimItemRowForecast | undefined;
  onViewSource?: (record: ExtractedRecord) => void;
  sourceType?: "spreadsheet" | "pdf";
  /** The projection's paid/incurred windows (#7436) — names the side of the window a flagged line fell on. */
  policyWindows?: PolicyWindows | null;
  /** The rail's "Dates are right" has been clicked: flagged lines drop the warning tone. */
  outsidePolicyAcknowledged?: boolean;
  /** Reviewer exclusions: an excluded line is struck through and reads Excluded, as on the Claims tab. */
  isExcluded?: (record: ExtractedRecord) => boolean;
}

const MAX_LINES = 200;

const str = (v: unknown): string | null => (v === null || v === undefined || v === "" ? null : String(v));

const num = parseMoneyCell;

/**
 * Date from a raw extracted cell. Extraction leaves dates as the file had
 * them — ISO, "12/29/2025", or an Excel serial like 45655 — so route through
 * the shared getExcelDate parser (zone-independent for serials and calendar
 * strings, #7467); anything it cannot read shows verbatim.
 */
const date = (v: unknown): string | null => {
  if (v === null || v === undefined || v === "") return null;
  const serial = typeof v === "number" ? v : /^\d{4,6}$/.test(String(v).trim()) ? Number(String(v).trim()) : null;
  if (serial !== null && serial > 0) return getExcelDate(serial, DATE_FORMATS.DISPLAY_LONG);
  // Calendar strings (ISO date-only, US m/d/y) are local dates, not instants;
  // the helper parses them zone-free and only treats full timestamps as instants.
  try {
    return getExcelDate(String(v), DATE_FORMATS.DISPLAY_LONG);
  } catch {
    return String(v);
  }
};

/** "Row 66" / "Page 3" from the record's extraction location, when known. */
export const sourceLabelFor = (record: ExtractedRecord, sourceType: "spreadsheet" | "pdf" = "spreadsheet"): string => {
  const fields = record.extracted_fields ?? [];
  if (sourceType === "pdf") {
    const page = fields.map((f) => f.source?.page_number).find((p) => typeof p === "number");
    return typeof page === "number" ? `Page ${page}` : "Source";
  }
  // Same resolution order as the preview drawer (sourcePreviewUtils.resolveSheetRow):
  // line_item_row_index first, then the first coordinate's y. Absolute 0-based
  // sheet row → 1-based like the spreadsheet's own gutter.
  const row =
    fields.map((f) => f.source?.line_item_row_index).find((r) => typeof r === "number") ??
    fields.map((f) => f.source?.coordinates?.[0]?.y).find((y) => typeof y === "number");
  return typeof row === "number" ? `Row ${row + 1}` : "Source";
};

const isOnFile = (fc: ClaimItemRowForecast | undefined): boolean =>
  !!fc && (fc.operation === "update" || !!fc.is_duplicate_on_file);

const GRID = "minmax(96px, 120px) minmax(96px, 120px) minmax(160px, 1fr) 110px minmax(120px, 190px) 90px";

export const ClaimLinesPanel = ({
  parentRecord,
  lines,
  projection,
  getClaimItemRowForRecord,
  onViewSource,
  sourceType = "spreadsheet",
  policyWindows,
  outsidePolicyAcknowledged = false,
  isExcluded,
}: ClaimLinesPanelProps) => {
  const claimant = getRecordData(parentRecord, TargetSchema.CLAIMANT);
  const memberId = str(claimant.member_id) ?? str(claimant.rx_member_id);
  const dob = date(claimant.claimant_birth_date);
  const rel =
    relationshipLabel(projection?.claimant_relationship) ?? relationshipLabel(str(claimant.claimant_relationship));
  const familyTotal =
    projection?.deductible_source === "family" && typeof projection.family_ytd_after === "number"
      ? projection.family_ytd_after
      : null;

  // Label/value pairs, not a dot-joined sentence: a reviewer scans for one
  // fact ("what's the member ID?") and should find it by its label.
  // The claim-line count is NOT repeated here: the name's sub-line already
  // carries it, and the row's expand badge did too.
  // Subscriber ID is no longer a top-row column (#7435) and "Other fields" is
  // suppressed when claim lines exist, so it surfaces here — it is the one
  // thing a reviewer needs on a "subscriber not found" row (!4924 review).
  const subscriberId = str(claimant.claimant_related_subscriber_id);
  const facts: Array<{ label: string; value: string }> = [];
  if (memberId) facts.push({ label: "Member ID", value: memberId });
  if (subscriberId) facts.push({ label: "Subscriber ID", value: subscriberId });
  if (rel) facts.push({ label: "Relationship", value: rel });
  if (dob) facts.push({ label: "Born", value: dob });
  if (familyTotal !== null) facts.push({ label: "Family total", value: formatUSD(familyTotal) });

  // New lines first, on-file after — same order the generic table used.
  const ordered = getClaimItemRowForRecord
    ? [...lines].sort(
        (a, b) => Number(isOnFile(getClaimItemRowForRecord(a))) - Number(isOnFile(getClaimItemRowForRecord(b)))
      )
    : lines;
  const shown = ordered.slice(0, MAX_LINES);

  const cell = { fontSize: 13, whiteSpace: "nowrap" as const, overflow: "hidden", textOverflow: "ellipsis" };
  const head = { ...cell, fontWeight: 600, color: "text.secondary" };

  return (
    <Box sx={{ py: 0.5 }} data-testid="claim-lines-panel">
      {/* Facts sit in their own band — more air below than the table's row
          rhythm, plus a rule — so the eye reads "who" first, then "what". */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          columnGap: 3,
          rowGap: 0.5,
          pb: 1.5,
          mb: 2,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        {facts.map((f) => (
          <Box key={f.label} sx={{ display: "flex", alignItems: "baseline", gap: 0.75, whiteSpace: "nowrap" }}>
            <Typography component="span" variant="caption" color="text.secondary">
              {f.label}
            </Typography>
            <Typography component="span" variant="body2">
              {f.value}
            </Typography>
          </Box>
        ))}
      </Box>

      {lines.length > 0 && (
        <Box role="table" aria-label="Claim lines" sx={{ display: "grid", gridTemplateColumns: GRID, columnGap: 2 }}>
          <Box role="row" sx={{ display: "contents" }}>
            <Typography role="columnheader" sx={head}>
              Service date
            </Typography>
            <Typography role="columnheader" sx={head}>
              Paid date
            </Typography>
            <Typography role="columnheader" sx={head}>
              Procedure
            </Typography>
            <Typography role="columnheader" sx={{ ...head, textAlign: "right" }}>
              Paid
            </Typography>
            <Typography role="columnheader" sx={head}>
              Counts toward deductible
            </Typography>
            <Typography role="columnheader" sx={{ ...head, textAlign: "right" }}>
              Source
            </Typography>
          </Box>
          {shown.map((line, idx) => {
            const d = getRecordData(line, TargetSchema.CLAIM_ITEM);
            const service = date(d.date_of_service ?? d.date_of_service_start);
            const paidDate = date(d.paid_date);
            const code = str(d.procedure_code);
            const desc = str(d.procedure_description);
            const procedure = [code, desc].filter(Boolean).join(" ") || "—";
            const paid = num(d.paid_amount);
            const forecast = getClaimItemRowForRecord?.(line);
            const onFile = isOnFile(forecast);
            const excluded = !!isExcluded?.(line);
            // Counts toward deductible (#7436 AC3): a verdict only once the
            // projection has one for the line; "—" while it hasn't. An excluded
            // line reads "Excluded" here — it will not load, so the question
            // does not arise, and this column is where the eye already is.
            const outside = excluded ? null : outsidePolicyLineFor(line, forecast, policyWindows);
            // One status column (walkthrough 2026-10-20): Excluded, then On
            // file (the loader skips it; what counts is the line already on
            // file), then the verdict.
            const counts = excluded
              ? "Excluded"
              : onFile
                ? "On file"
                : !forecast
                  ? "—"
                  : outside
                    ? `No · ${outsideWindowPhrase(outside.reason, outside.side)}`
                    : "Yes";
            const countsTone = outside
              ? outsidePolicyAcknowledged
                ? "text.secondary"
                : "warning.dark"
              : "text.primary";
            const rowSx = {
              display: "contents" as const,
              "& > *": {
                py: 0.75,
                borderTop: 1,
                borderColor: "divider",
                opacity: onFile || excluded ? 0.6 : 1,
                ...(excluded ? { textDecoration: "line-through" } : {}),
              },
            };
            return (
              <Box role="row" key={idx} sx={rowSx} data-excluded={excluded || undefined}>
                <Typography role="cell" sx={cell}>
                  {service ?? "—"}
                </Typography>
                <Typography role="cell" sx={cell}>
                  {paidDate ?? "—"}
                </Typography>
                <Typography role="cell" sx={cell} title={procedure}>
                  {procedure}
                </Typography>
                <Typography role="cell" sx={{ ...cell, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                  {paid !== null ? formatUSD(paid) : "—"}
                </Typography>
                <Typography
                  role="cell"
                  sx={{
                    ...cell,
                    color: excluded || onFile ? "text.secondary" : countsTone,
                    fontWeight: outside && !outsidePolicyAcknowledged ? 600 : 400,
                    textDecoration: excluded ? "none" : undefined,
                  }}
                  data-testid="claim-line-counts"
                >
                  {counts}
                </Typography>
                <Box role="cell" sx={{ textAlign: "right" }}>
                  {onViewSource ? (
                    <Link
                      component="button"
                      type="button"
                      variant="body2"
                      onClick={() => onViewSource(line)}
                      sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, fontSize: 13 }}
                      aria-label={`View ${sourceLabelFor(line, sourceType).toLowerCase()} in the source file`}
                    >
                      {sourceLabelFor(line, sourceType)}
                      {sourceType === "pdf" ? <PdfIcon sx={{ fontSize: 14 }} /> : <SheetIcon sx={{ fontSize: 14 }} />}
                    </Link>
                  ) : (
                    <Typography sx={{ ...cell, color: "text.secondary" }}>
                      {sourceLabelFor(line, sourceType)}
                    </Typography>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
      {lines.length > MAX_LINES && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
          Showing the first {MAX_LINES} of {lines.length} claim lines. Use the Claims tab for the rest.
        </Typography>
      )}
    </Box>
  );
};
